import { t } from "@lingui/core/macro";
import { confirmSignIn } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";

import { type ChallengedUser } from "@/routes/_unauthenticated/sign-in";
import { AsyncButton } from "@/shared/components/ui/async-button";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";

import AuthPage from "./AuthPage";

export default function ConfirmSignIn({
  challengedUser,
  setChallengedUser,
}: {
  challengedUser: ChallengedUser;
  setChallengedUser: (user: ChallengedUser | null) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const handleConfirmSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const code = formData.get("code") as string;

    setError("");
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.isSignedIn) setChallengedUser(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage title={t`Enter Authenticator Code`}>
      <p className="mb-4 text-center text-sm text-gray-500">{t`Please open your third-party authenticator app to receive the code.`}</p>
      <form className="flex flex-col gap-4" onSubmit={handleConfirmSignIn}>
        <Input
          type="number"
          placeholder={t`Enter your Authenticator Code`}
          name="code"
          autoComplete="one-time-code"
          required
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
        <AsyncButton type="submit" loading={loading} loadingText={t`Submitting`}>
          {t`Submit`}
        </AsyncButton>
        <Button
          variant="link"
          type="button"
          onClick={() => {
            setChallengedUser({
              challengeName: "DISABLE_MFA",
              email: challengedUser.email,
            });
          }}
        >
          {t`Have recovery code?`}
        </Button>
        <Button
          variant="link"
          type="button"
          onClick={() => {
            setChallengedUser(null);
          }}
        >
          {t`Back to Sign In`}
        </Button>
      </form>
    </AuthPage>
  );
}
